
import { defineComponent, onBeforeMount, onBeforeUnmount, onUnmounted, reactive } from 'vue'
import ymaps from 'yandex-maps'

// components
import Tabs from '@/common/components/UI/Tabs/index.vue'
import TheMap from '@/common/components/UI/TheMap/index.vue'
import MapGeocoder from '@/common/components/UI/MapGeocoder/index.vue'

// module components
import SectorPanel from '@/modules/DispatcherModule/components/SectorPanel/index.vue'
import SetAddressPanel from '@/modules/DispatcherModule/components/SetAddressPanel/index.vue'

// types
import { SectorT } from '@/core/types/sectors.types'
import { DispatcherSectorPayloadT, DispatcherSectorT } from '@/core/types/dispatchers_sector.types'

// constants
import {
    ROUTES_TABS,
    COURIER_SECTOR_TYPE,
    DISPATCHER_SECTOR_TYPE,
} from '@/core/constants/dispatcher.constants'
import { YANDEX_MAP_TYPE, TWO_GIS_MAP_TYPE } from '@/core/constants/map.constants'

// composable
import useDispatcherMap from '@/modules/DispatcherModule/composable/useDispatcherMap'

// enums
import { DISPATCHER_EVENTS } from '@/core/enums/dispatcher.enums'

// event bus
import emitter from 'tiny-emitter/instance'

// realisations
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { sectors } from '@/core/realisations/sectors.realisation'
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'

export default defineComponent({
    components: {
        tabs: Tabs,
        'the-map': TheMap,
        'sector-panel': SectorPanel,
        'set-address-panel': SetAddressPanel,
        'map-geocoder': MapGeocoder,
    },
    setup() {
        const state = reactive({
            is_sector_panel_showed: false,
            is_add_sector: false,
            is_sector_saving: false,
            sector: null as SectorT | DispatcherSectorT | null,
            sector_index: -1,
            sector_type: COURIER_SECTOR_TYPE,
            unfinished_sector: null as { color: string; coordinates: number[][] } | null,
        })

        const dispatcher_map = useDispatcherMap()

        const onMapReady = (map_instance: ymaps.Map) => {
            dispatcher_map.setInstance(map_instance)
            dispatcher_map.addPaintModule()

            emitter.emit(DISPATCHER_EVENTS.DISPATCHER_MAP_READY)
        }

        const closeSectorPanel = () => {
            return new Promise<void>((resolve, reject) => {
                try {
                    state.is_sector_panel_showed = false
                    state.is_add_sector = false
                    state.sector = null
                    state.sector_index = -1
                    state.sector_type = COURIER_SECTOR_TYPE

                    resolve()
                } catch (error) {
                    reject(error)
                }
            })
        }

        const stopSectorAction = () => {
            if (state.is_add_sector) {
                if (state.unfinished_sector) {
                    dispatcher_map.removeNewPolygon()
                    state.unfinished_sector = null
                }

                emitter.emit(DISPATCHER_EVENTS.DISPATCHER_STOP_ADD_SECTOR)
            } else {
                emitter.emit(DISPATCHER_EVENTS.DISPATCHER_STOP_EDIT_SECTOR)
            }

            closeSectorPanel()
        }

        const onSaveSector = async ({
            name,
            color,
            description,
            cityId,
            userIds,
        }: {
            name: string
            color: string
            description?: string
            cityId?: number
            userIds?: number[]
        }) => {
            try {
                state.is_sector_saving = true

                const coordinates = dispatcher_map.getPolygonCoordinates(state.sector_index)

                if (state.sector_type === COURIER_SECTOR_TYPE) {
                    if (!state.sector) return

                    const payload = {
                        name,
                        color,
                        coordinates,
                        // @ts-ignore
                        dispatchers_sector_id: state.sector.dispatchers_sector_id,
                    }

                    await sectors.updateSector(state.sector.id, payload)
                } else {
                    if (!state.sector || !description) return

                    const payload: DispatcherSectorPayloadT = {
                        name,
                        description,
                        coordinates,
                    }

                    if (cityId) payload.cityId = cityId
                    if (userIds) payload.userIds = userIds

                    // dispatcher sector request
                    await dispatchers_sectors.updateSector(state.sector.id, payload)
                }

                dispatcher_map.updatePolygon({
                    polygon_index: state.sector_index,
                    color,
                    sector_name: description ? `${name} (${description})` : name,
                })

                emitter.emit(DISPATCHER_EVENTS.DISPATCHER_COMPLETE_EDIT_SECTOR, {
                    index: state.sector_index,
                    name,
                    color,
                    description,
                })

                await stopSectorAction()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_sector_saving = false
            }
        }

        const onCreateSector = async ({
            name,
            color,
            dispatchers_sector_id,
            description,
        }: {
            name: string
            color: string
            dispatchers_sector_id: number
            description: string
        }) => {
            try {
                state.is_sector_saving = true

                const coordinates = dispatcher_map.getNewPolygonCoordinates()

                if (state.sector_type === COURIER_SECTOR_TYPE) {
                    const payload = {
                        name,
                        color,
                        coordinates,
                        dispatchers_sector_id,
                    }

                    await sectors.createSector(payload)
                } else {
                    const payload = {
                        name,
                        color,
                        coordinates,
                        description,
                    }

                    await dispatchers_sectors.createSector(payload)
                }

                state.unfinished_sector = null
                emitter.emit(DISPATCHER_EVENTS.DISPATCHER_COMPLETE_ADD_SECTOR)
                stopSectorAction()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_sector_saving = false
            }
        }

        onBeforeMount(() => {
            /**
             * Слушатель начала редактирования сектора (курьерксий / диспетчерский)
             * @param sector Объект сектора
             * @param sector_index Индекс сектора
             * @param sector_type Тип сектора (COURIER_SECTOR_TYPE / DISPATCHER_SECTOR_TYPE)
             */
            emitter.on(
                DISPATCHER_EVENTS.DISPATCHER_START_EDIT_SECTOR,
                async ({
                    sector,
                    sector_index,
                    sector_type,
                }: {
                    sector: SectorT | DispatcherSectorT
                    sector_index: number
                    sector_type: string
                }) => {
                    await closeSectorPanel()

                    state.is_add_sector = false

                    state.sector = sector
                    state.sector_index = sector_index
                    state.sector_type = sector_type

                    state.is_sector_panel_showed = true
                }
            )

            /**
             * Слушатель начала добавления сектора (курьерксий / диспетчерский)
             * @param sector_type Тип сектора (COURIER_SECTOR_TYPE / DISPATCHER_SECTOR_TYPE)
             */
            emitter.on(
                DISPATCHER_EVENTS.DISPATCHER_START_ADD_SECTOR,
                async ({ sector_type }: { sector_type: string }) => {
                    await closeSectorPanel()

                    state.sector_type = sector_type
                    state.is_add_sector = true
                    state.is_sector_panel_showed = true
                }
            )
        })

        const onSectorPainted = ({
            coordinates,
            color,
        }: {
            coordinates: number[][]
            color: string
        }): void => {
            state.unfinished_sector = {
                color,
                coordinates,
            }

            dispatcher_map.drawSector({ coordinates, color })
        }

        const onStartPaint = ({ color }: { color: string }) => {
            dispatcher_map.startPaint({ color, callback: onSectorPainted })
        }

        const onCancelSetAddress = () => {
            dispatcher_map.cancelSetAddress()
        }

        const onSelectAddress = (item: {
            name: string
            description?: string
            coordinates: number[]
        }) => {
            dispatcher_map.setAddressByResult(item)
        }

        const onSaveAddress = async () => {
            if (
                !dispatcher_map.setting_address_state.order ||
                !dispatcher_map.setting_address_state.new_address
            )
                return

            try {
                dispatcher_map.setting_address_state.is_saving = true

                const coordinates = dispatcher_map.getNewAddressCoordinates()
                if (!coordinates) return

                const payload = {
                    title: dispatcher_map.setting_address_state.order.sender_title,
                    city_id: dispatcher_map.setting_address_state.order.sender_city_id,
                    phone: dispatcher_map.setting_address_state.order.sender_phone,
                    full_name: dispatcher_map.setting_address_state.order.sender_full_name,
                    latitude: coordinates.latitude.toString(),
                    longitude: coordinates.longitude.toString(),
                    full_address: dispatcher_map.setting_address_state.new_address,
                }

                await dispatcher.setOrderAddress(
                    dispatcher_map.setting_address_state.order.sender_id,
                    payload
                )

                emitter.emit(DISPATCHER_EVENTS.DISPATCHER_SAVE_ADDRESS)
                dispatcher_map.cancelSetAddress()
            } catch (error) {
                console.error(error)
            } finally {
                dispatcher_map.setting_address_state.is_saving = false
            }
        }

        const onMapChange = (map: string) => {
            dispatcher_map.setMap(map)
        }

        onBeforeUnmount(() => {
            emitter.off(DISPATCHER_EVENTS.DISPATCHER_START_EDIT_SECTOR)
            emitter.off(DISPATCHER_EVENTS.DISPATCHER_START_ADD_SECTOR)
        })

        onUnmounted(() => {
            dispatcher_map.setInstance(undefined)
        })

        return {
            ROUTES_TABS,
            state,
            COURIER_SECTOR_TYPE,
            DISPATCHER_SECTOR_TYPE,
            YANDEX_MAP_TYPE,
            TWO_GIS_MAP_TYPE,
            onMapReady,
            onMapChange,
            stopSectorAction,
            onSaveSector,
            onCreateSector,
            onStartPaint,
            dispatcher_map,
            onCancelSetAddress,
            onSaveAddress,
            onSelectAddress,
        }
    },
})
